import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SiteBanner {
  id: string;
  message: string;
  link_url: string | null;
  link_text: string | null;
  bg_color: string;
  text_color: string;
  is_dismissible: boolean;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_by: string | null;
}

export function useSiteBanners() {
  return useQuery({
    queryKey: ["site-banners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_banners")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as SiteBanner[];
    },
    staleTime: 30_000,
  });
}

export function useActiveBanners() {
  return useQuery({
    queryKey: ["site-banners", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_banners")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      const now = new Date();
      return (data as SiteBanner[]).filter((b) => {
        if (b.starts_at && new Date(b.starts_at) > now) return false;
        if (b.ends_at && new Date(b.ends_at) < now) return false;
        return true;
      });
    },
    staleTime: 30_000,
  });
}

export function useUpsertBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (b: Partial<SiteBanner> & { message: string }) => {
      if (b.id) {
        const { error } = await supabase.from("site_banners").update(b).eq("id", b.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("site_banners").insert(b);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-banners"] }),
  });
}

export function useDeleteBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("site_banners").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-banners"] }),
  });
}
